import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { actionReparacionesGet } from "../../redux/actions/reparaciones/reparaciones";

import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showHerramientasEffect } from "../../components/utils/antWaveEffect";
import "./PaquetesPage.css";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, #3d0011 0%, #5c0019 100%)";

const ESTADO_EN_PROCESO = [1, 2, 3];
const ESTADO_LISTO = 4;
const ESTADO_ENTREGADO = 5;
const ESTADO_CANCELADO = 6;

const ESTADO_STRIP = {
  1: "is-red", 2: "is-red", 3: "is-red",
  4: "is-green",
  5: "is-green",
  6: "is-red",
};

export default function ReparacionesPage() {
  const dispatch = useDispatch();
  const { items = [], loading } = useSelector((state) => state.reparaciones);
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchParams = useMemo(() => ({ search: searchDebounced || undefined }), [searchDebounced]);

  useEffect(() => {
    const key = JSON.stringify(fetchParams);
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    dispatch(actionReparacionesGet(fetchParams));
  }, [dispatch, fetchParams]);

  const counts = useMemo(() => ({
    total: items.length,
    enProceso: items.filter((r) => ESTADO_EN_PROCESO.includes(r.id_estado_reparacion)).length,
    listas: items.filter((r) => r.id_estado_reparacion === ESTADO_LISTO).length,
    entregadas: items.filter((r) => r.id_estado_reparacion === ESTADO_ENTREGADO).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "en_proceso") return items.filter((r) => ESTADO_EN_PROCESO.includes(r.id_estado_reparacion));
    if (activeFilter === "listas") return items.filter((r) => r.id_estado_reparacion === ESTADO_LISTO);
    if (activeFilter === "entregadas") return items.filter((r) => r.id_estado_reparacion === ESTADO_ENTREGADO);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de reparaciones", icon: <i className="fa-solid fa-screwdriver-wrench" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "en_proceso", label: "En proceso", icon: <i className="fa-solid fa-clock" />, iconCls: "paq-stat-icon-barra", count: counts.enProceso },
    { key: "listas", label: "Listas para entrega", icon: <i className="fa-solid fa-circle-check" />, iconCls: "paq-stat-icon-decoracion", count: counts.listas },
    { key: "entregadas", label: "Entregadas", icon: <i className="fa-solid fa-box-open" />, iconCls: "paq-stat-icon-receta", count: counts.entregadas },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showHerramientasEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Reparaciones</Title>
                <Text className="paq-subtitle">Seguimiento de reparaciones recibidas en el taller.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por folio, cliente o tipo de aparato..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Reparaciones ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} reparaciones encontradas</Text>
                </div>
                <div className="paq-toolbar-right">
                  <>
                    <Button
                      type="primary"
                      icon={<i className="fa-solid fa-plus" />}
                      onClick={() => navigate("/app/reparaciones/crear")}
                      style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
                    >
                      Recibir equipo
                    </Button>
                  </>
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin reparaciones que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.length > 0 && (
                    <>
                      {filteredItems.map((r) => (
                        <div key={r.id_reparacion} className="paq-card">
                          <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                            <i className="fa-solid fa-screwdriver-wrench paq-card-banner-icon" />
                          </div>

                          <div className="paq-card-header">
                            <div className="paq-card-head">
                              <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{r.tipo_aparato}</Paragraph>
                              <span className="paq-tipo-badge paq-tipo-sonido">{r.numero_trabajo}</span>
                            </div>
                          </div>

                          <div className="paq-card-body">
                            <div className="paq-contenidos-box">
                              <i className="fa-solid fa-user paq-info-icon" />
                              <div className="paq-info-text">
                                <span className="paq-info-label">Cliente</span>
                                <span className="paq-info-value">{r.cliente_nombre}</span>
                              </div>
                            </div>
                            {(r.marca || r.modelo) && (
                              <div className="paq-contenidos-box">
                                <i className="fa-solid fa-tag paq-info-icon" />
                                <div className="paq-info-text">
                                  <span className="paq-info-label">Marca / Modelo</span>
                                  <span className="paq-info-value">{[r.marca, r.modelo].filter(Boolean).join(" / ")}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="paq-card-footer">
                            <div className={`paq-bottom-strip ${ESTADO_STRIP[r.id_estado_reparacion] || "is-red"}`}>
                              <span className="paq-strip-label">{r.estado_nombre}</span>
                            </div>
                            <Button
                              className="paq-btn-details"
                              icon={<i className="fa-solid fa-circle-info" />}
                              onClick={() => navigate(`/app/reparaciones/${r.id_reparacion}`)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
